import { useMemo } from 'react';
import { useSymbolMapping } from '../contexts/SymbolMappingContext';

/**
 * Returns the ETF/index underlying to use for API fetches,
 * and the display label (futures ticker when futures mode is ON).
 * Components should fetch with `fetchSymbol` and display `displaySymbol`.
 */
export function useFuturesAwareSymbol(symbol: string) {
  const { isFuturesMode, getOptionsUnderlyingForFuture, getFuturesLabel } = useSymbolMapping();

  return useMemo(() => {
    if (!isFuturesMode || !symbol) {
      return { fetchSymbol: symbol, displaySymbol: symbol, isFutures: false };
    }
    const futuresLabel = getFuturesLabel(symbol);
    if (futuresLabel) {
      return { fetchSymbol: symbol, displaySymbol: futuresLabel, isFutures: true };
    }
    const etf = getOptionsUnderlyingForFuture(symbol);
    if (etf !== symbol) {
      return { fetchSymbol: etf, displaySymbol: symbol, isFutures: true };
    }
    return { fetchSymbol: symbol, displaySymbol: symbol, isFutures: false };
  }, [isFuturesMode, symbol, getFuturesLabel, getOptionsUnderlyingForFuture]);
}

/**
 * Returns the list of ETF underlyings to fetch for all selected futures,
 * plus a map from ETF -> futures display label.
 */
export function useFuturesUnderlyings() {
  const { isFuturesMode, selectedFutures, getOptionsUnderlyingForFuture, getSelectedETFUnderlyings } = useSymbolMapping();

  return useMemo(() => {
    if (!isFuturesMode || !selectedFutures.length) {
      return { underlyings: [] as string[], labelMap: {} as Record<string, string>, active: false };
    }
    const underlyings = getSelectedETFUnderlyings();
    const labelMap: Record<string, string> = {};
    for (const ft of selectedFutures) {
      const etf = getOptionsUnderlyingForFuture(ft);
      labelMap[etf.toUpperCase()] = ft;
    }
    return { underlyings, labelMap, active: true };
  }, [isFuturesMode, selectedFutures, getOptionsUnderlyingForFuture, getSelectedETFUnderlyings]);
}
